import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Consumo da API de IA (at-12) — a tela "Consumo de IA" da Administração do Sistema.
 * Sem catch nenhum aqui de propósito (regra da at-60): "não há consumo" e "não consegui
 * perguntar" levam a leituras opostas — quem trata o erro é a tela.
 */
export interface TotaisDeConsumo {
  operacoes: number;
  idasAoModelo: number;
  tokensEntrada: number;
  tokensSaida: number;
  cacheLido: number;
  cacheEscrito: number;
  custoEstimadoUsd: number;
}

export interface ConsumoPorDia extends TotaisDeConsumo {
  dia: string; // YYYY-MM-DD no fuso da oficina
}

export interface ConsumoPorOrigem extends TotaisDeConsumo {
  origem: string;
}

export interface ConsumoPorModelo extends TotaisDeConsumo {
  modelo: string;
}

export interface PrecoDeModelo {
  modelo: string;
  entradaPorMilhao: number;
  saidaPorMilhao: number;
  escritaDeCachePorMilhao: number;
  leituraDeCachePorMilhao: number;
}

export interface ConsumoPorMes {
  /** `YYYY-MM` no fuso da oficina. */
  mes: string;
  total: TotaisDeConsumo;
  origens: ConsumoPorOrigem[];
}

export interface ResumoDeConsumo {
  janelaDias: number;
  /** Quantos meses a quebra por mês cobre (contac/at-13). */
  mesesDaQuebra: number;
  moeda: string;
  hoje: TotaisDeConsumo;
  mesCorrente: TotaisDeConsumo;
  ultimos30Dias: TotaisDeConsumo;
  porDia: ConsumoPorDia[];
  porOrigem: ConsumoPorOrigem[];
  porModelo: ConsumoPorModelo[];
  /** Quebra mensal por origem (contac/at-13): responde "quanto do mês veio de cada Radar". */
  porMesEOrigem: ConsumoPorMes[];
  /** Últimos 30 dias, por quem pediu (20/10/2026). Ausente em backend anterior a esta versão. */
  porUsuario?: ConsumoPorUsuario[];
  precos: PrecoDeModelo[];
  modelosSemPreco: string[];
}

export async function fetchResumoDeConsumo(): Promise<ResumoDeConsumo> {
  const { data } = await axios.get<ResumoDeConsumo>(`${BACKEND_URL}/consumo-ia/resumo`, {
    headers: authHeader(),
  });
  return data;
}

/**
 * Por usuário (20/10/2026, "quem utilizou"). `usuarioId` nulo = o que o sistema fez sozinho (rotina
 * diária do Radar, redação dos cartões) e os registros anteriores a esta versão.
 */
export interface ConsumoPorUsuario extends TotaisDeConsumo {
  usuarioId: string | null;
  nome: string | null;
}

export interface OperacaoDeConsumo {
  id: string;
  ocorridoEm: string;
  origem: string;
  modelo: string;
  usuarioId: string | null;
  usuarioNome: string | null;
  idasAoModelo: number;
  tokensEntrada: number;
  tokensSaida: number;
  cacheLido: number;
  cacheEscrito: number;
  /** Nulo quando o modelo não está na tabela local de preços. */
  custoEstimadoUsd: number | null;
}

export interface PaginaDeOperacoes {
  pagina: number;
  porPagina: number;
  total: number;
  itens: OperacaoDeConsumo[];
}

/** Sem catch: "nenhuma operação" e "não consegui perguntar" levam a leituras opostas. */
export async function fetchOperacoesDeConsumo(pagina: number, porPagina = 20): Promise<PaginaDeOperacoes> {
  const { data } = await axios.get<PaginaDeOperacoes>(`${BACKEND_URL}/consumo-ia/operacoes`, {
    headers: authHeader(),
    params: { pagina, porPagina },
  });
  return data;
}
